/**
 * Runtime GraphQL enum values extracted from the schema.
 *
 * Pattern: const object + derived type for each enum.
 *
 * These constants are decoupled from the schema once written. Schema additions
 * must be reflected here manually. `gql.tada` will surface most divergences as
 * type errors where these consts meet operation result types, but it cannot
 * detect missing entries on its own.
 */

export const AskableVideoAccessPolicy = {
  Join: 'join',
  PlaybackView: 'playback_view',
  Request: 'request',
  Start: 'start',
} as const;
export type AskableVideoAccessPolicy = (typeof AskableVideoAccessPolicy)[keyof typeof AskableVideoAccessPolicy];

export const AskableVideoMethods = {
  AiModerated: 'ai_moderated',
  Moderated: 'moderated',
  Unmoderated: 'unmoderated',
} as const;
export type AskableVideoMethods = (typeof AskableVideoMethods)[keyof typeof AskableVideoMethods];

export const AskableVideoUserStatus = {
  Approved: 'approved',
  Cancelled: 'cancelled',
  Denied: 'denied',
  Requested: 'requested',
} as const;
export type AskableVideoUserStatus = (typeof AskableVideoUserStatus)[keyof typeof AskableVideoUserStatus];

export const AskableVideoUserType = {
  Facilitator: 'facilitator',
  Observer: 'observer',
  Participant: 'participant',
} as const;
export type AskableVideoUserType = (typeof AskableVideoUserType)[keyof typeof AskableVideoUserType];

export const BookingTaskResultStatus = {
  Completed: 'completed',
  Error: 'error',
  Loaded: 'loaded',
  Pending: 'pending',
  Skipped: 'skipped',
  Started: 'started',
} as const;
export type BookingTaskResultStatus = (typeof BookingTaskResultStatus)[keyof typeof BookingTaskResultStatus];

export const CollectionIconTypes = {
  Emoji: 'emoji',
  Icon: 'icon',
} as const;
export type CollectionIconTypes = (typeof CollectionIconTypes)[keyof typeof CollectionIconTypes];

export const CollectionsConfigSort = {
  CreatedAtAsc: 'created_at_asc',
  CreatedAtDesc: 'created_at_desc',
} as const;
export type CollectionsConfigSort = (typeof CollectionsConfigSort)[keyof typeof CollectionsConfigSort];

export const CollectionTitleTheme = {
  Dark: 'dark',
  Light: 'light',
} as const;
export type CollectionTitleTheme = (typeof CollectionTitleTheme)[keyof typeof CollectionTitleTheme];

export const ConditionalLogicMcqType = {
  All: 'all',
  Any: 'any',
} as const;
export type ConditionalLogicMcqType = (typeof ConditionalLogicMcqType)[keyof typeof ConditionalLogicMcqType];

export const HighlightClipStatus = {
  Completed: 'completed',
  Failed: 'failed',
  Pending: 'pending',
  Processing: 'processing',
} as const;
export type HighlightClipStatus = (typeof HighlightClipStatus)[keyof typeof HighlightClipStatus];

export const HighlightEventType = {
  HighlightCreate: 'highlight_create',
  HighlightDelete: 'highlight_delete',
  HighlightUpdate: 'highlight_update',
} as const;
export type HighlightEventType = (typeof HighlightEventType)[keyof typeof HighlightEventType];

export const InsightIconTypes = {
  Emoji: 'emoji',
  Icon: 'icon',
} as const;
export type InsightIconTypes = (typeof InsightIconTypes)[keyof typeof InsightIconTypes];

export const InsightTitleTheme = {
  Dark: 'dark',
  Light: 'light',
} as const;
export type InsightTitleTheme = (typeof InsightTitleTheme)[keyof typeof InsightTitleTheme];

export const Locale = {
  Ar: 'ar',
  Cs: 'cs',
  Da: 'da',
  De: 'de',
  En: 'en',
  Es: 'es',
  Fr: 'fr',
  Hi: 'hi',
  Hu: 'hu',
  It: 'it',
  Ja: 'ja',
  Ko: 'ko',
  Nl: 'nl',
  Pt: 'pt',
  Sk: 'sk',
  Tr: 'tr',
  ZhCn: 'zh_CN',
  ZhHk: 'zh_HK',
} as const;
export type Locale = (typeof Locale)[keyof typeof Locale];

export const SegmentFilterMethod = {
  AnyOf: 'any_of',
  Contains: 'contains',
  DoesNotContain: 'does_not_contain',
  EqualTo: 'equal_to',
  GreaterOrEqualTo: 'greater_or_equal_to',
  GreaterThan: 'greater_than',
  LessOrEqualTo: 'less_or_equal_to',
  LessThan: 'less_than',
  NoneOf: 'none_of',
  NotEqualTo: 'not_equal_to',
} as const;
export type SegmentFilterMethod = (typeof SegmentFilterMethod)[keyof typeof SegmentFilterMethod];

export const ShareRequesterType = {
  Domain: 'domain',
  Email: 'email',
  Team: 'team',
} as const;
export type ShareRequesterType = (typeof ShareRequesterType)[keyof typeof ShareRequesterType];

export const ShareRequestStatus = {
  Approved: 'approved',
  Rejected: 'rejected',
  Requested: 'requested',
} as const;
export type ShareRequestStatus = (typeof ShareRequestStatus)[keyof typeof ShareRequestStatus];

export const ShareStatus = {
  Invited: 'invited',
  Opened: 'opened',
  Revoked: 'revoked',
} as const;
export type ShareStatus = (typeof ShareStatus)[keyof typeof ShareStatus];

export const ShareType = {
  Collection: 'collection',
  Insight: 'insight',
} as const;
export type ShareType = (typeof ShareType)[keyof typeof ShareType];

export const SubmissionStatus = {
  Available: 'available',
  Completed: 'completed',
  Confirmed: 'confirmed',
  Excluded: 'excluded',
  HelpRequested: 'help_requested',
  InProgress: 'in_progress',
  InviteDeclined: 'invite_declined',
  Invited: 'invited',
  ParticipantCancelled: 'participant_cancelled',
  PendingCheck: 'pending_check',
  PendingTechCheck: 'pending_tech_check',
  Reported: 'reported',
  TimeExpired: 'time_expired',
  Waitlisted: 'waitlisted',
} as const;
export type SubmissionStatus = (typeof SubmissionStatus)[keyof typeof SubmissionStatus];

export const SystemBlockType = {
  ThankYou: 'thank_you',
  Welcome: 'welcome',
} as const;
export type SystemBlockType = (typeof SystemBlockType)[keyof typeof SystemBlockType];

export const TagEventType = {
  TagsCreate: 'tags_create',
  TagsDelete: 'tags_delete',
  TagsUpdate: 'tags_update',
} as const;
export type TagEventType = (typeof TagEventType)[keyof typeof TagEventType];

export const TaskBlockType = {
  CardSort: 'card_sort',
  ContextScreen: 'context_screen',
  FigmaPrototype: 'figma_prototype',
  LiveMobileTest: 'live_mobile_test',
  LiveWebsiteTest: 'live_website_test',
  Matrix: 'matrix',
  MultipleChoiceQuestion: 'multiple_choice_question',
  OpenAnswer: 'open_answer',
  OpinionScale: 'opinion_scale',
  StackRank: 'stack_rank',
  SystemUsabilityScale: 'system_usability_scale',
  TreeTest: 'tree_test',
  VariantComparison: 'variant_comparison',
} as const;
export type TaskBlockType = (typeof TaskBlockType)[keyof typeof TaskBlockType];

export const UploadArea = {
  BookingTasks: 'booking_tasks',
  CustomAgreements: 'custom_agreements',
  Insights: 'insights',
  Profile: 'profile',
} as const;
export type UploadArea = (typeof UploadArea)[keyof typeof UploadArea];

export const UploadMimeType = {
  ApplicationPdf: 'application_pdf',
  ImageGif: 'image_gif',
  ImageJpg: 'image_jpg',
  ImagePng: 'image_png',
  ImageWebp: 'image_webp',
} as const;
export type UploadMimeType = (typeof UploadMimeType)[keyof typeof UploadMimeType];

export const UserType = {
  Admin: 'admin',
  Client: 'client',
  Participant: 'participant',
  Researcher: 'researcher',
  ShareViewer: 'share_viewer',
} as const;
export type UserType = (typeof UserType)[keyof typeof UserType];

export const VideoNoteSubscriptionType = {
  Create: 'create',
  Delete: 'delete',
  Update: 'update',
} as const;
export type VideoNoteSubscriptionType = (typeof VideoNoteSubscriptionType)[keyof typeof VideoNoteSubscriptionType];
